import { database } from "./database";
import { Collections } from "@/types/Collections.ts";
import {
  BaseFirestore,
  CreateTechnicianDto,
  PaginationOptions,
  Technician,
  TechnicianIndex,
  TypeSenseTechnician,
} from "@/models";
import { user } from "@/repositories/user.ts";
import { tenant } from "@/repositories/tenant.ts";
import { SortingState } from "@tanstack/react-table";
import { SearchParams } from "typesense/lib/Typesense/Documents";
import { typesense } from "@/services/typesense";
import { config } from "@/lib/config";
import { Address } from "@/types/Address";
import { Phone } from "@/types/Phone";

export const technician = {
  get: (id: string, tenantId: string) => {
    return database.get<Technician>(
      id,
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`
    );
  },
  create: async (technicianData: CreateTechnicianDto, tenantId: string) => {
    // Look for user
    const userData = await user.getByEmail(technicianData.email);

    const newTechnician: Omit<Technician, keyof BaseFirestore> = {
      ...technicianData,
      linkedUserId: userData?.id ?? "",
    };
    if (userData) {
      await tenant.addUserViaTechnician(
        tenantId,
        userData.id,
        "user",
        technicianData.email
      );
    }

    return database.create<Technician>(
      newTechnician,
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`
    );
  },
  getByEmail: async (email: string, tenantId: string) => {
    const technicians = await database.queryDocuments<Technician>(
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`,
      "email",
      "==",
      email
    );
    if (!technicians) {
      return undefined;
    }
    if (technicians.length === 0) {
      return undefined;
    }
    return technicians[0];
  },
  update: async (
    technicianId: string,
    data: Partial<Technician>,
    tenantId: string
  ) => {
    return database.update<Technician>(
      { id: technicianId, ...data },
      `${Collections.tenants}/${tenantId}/${Collections.technicians}/${technicianId}`
    );
  },
  getAll: async (tenantId: string) => {
    return database.list(
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`
    );
  },
  list: async (
    tenantId: string,
    paginationOptions: PaginationOptions<Technician>
  ) => {
    return database.paginate(
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`,
      {
        orderBy: "createdAt",
        ...paginationOptions,
      }
    );
  },
  subscribe: (
    technicianId: string,
    callback: (doc: Technician | undefined) => void,
    tenantId: string
  ) => {
    return database.subscribeDocument<Technician>(
      technicianId,
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`,
      callback
    );
  },
  getOptions: async (tenantId: string) => {
    return database.get<TechnicianIndex>(
      "index",
      `${Collections.tenants}/${tenantId}/${Collections.technicians}`
    );
  },
  convertTypesenseToTechnician: (doc: TypeSenseTechnician): Technician => {
    return {
      tenantId: doc.tenantId,
      firstName: doc.firstName,
      lastName: doc.lastName,
      displayName: doc.displayName,
      homeAddress: {
        address1: doc["homeAddress.address1"],
        address2: doc["homeAddress.address2"],
        state: doc["homeAddress.state"],
        city: doc["homeAddress.city"],
        zip: doc["homeAddress.zip"],
      },
      workAddress: {
        address1: doc["workAddress.address1"],
        address2: doc["workAddress.address2"],
        state: doc["workAddress.state"],
        city: doc["workAddress.city"],
        zip: doc["workAddress.zip"],
      },
      primaryPhone: {
        phoneNumber: doc["primaryPhone.phoneNumber"],
        type: doc["primaryPhone.type"],
      },
      email: doc.email,
      role: doc.role,
      _deleted: doc._deleted,
      updatedAt: new Date(doc.updatedAt * 1000),
      createdAt: new Date(doc.createdAt * 1000),
      updatedBy: doc.updatedBy,
      createdBy: doc.createdBy,
      id: doc.id,
      _source: doc._source,
    };
  },
  search: async (
    tenantId: string,
    searchString: string,
    page: number = 1,
    perPage: number = 10,
    orderBy?: SortingState
  ): Promise<{ data: Technician[]; count: number; total: number }> => {
    const searchParameters: SearchParams = {
      q: searchString,
      query_by: "firstName,lastName,email",
      filter_by: `tenantId:=${tenantId}`,
      sort_by: orderBy
        ? orderBy
            .map((item) => `${item.id}:${item.desc ? "desc" : "asc"}`)
            .join(",")
        : "",
      infix: "fallback",
      page,
      per_page: perPage,
    };
    const hits = await typesense
      .collections<TypeSenseTechnician>(config.typesense.collections.technician)
      .documents()
      .search(searchParameters);
    return {
      total: hits.found ?? 0,
      count: hits.hits?.length ?? 0,
      data:
        hits.hits?.map((d) =>
          technician.convertTypesenseToTechnician(d.document)
        ) ?? [],
    };
  },
  generateRandom: async (tenantId: string) => {
    if (config.devMode) {
      const { faker, simpleFaker } = await import("@faker-js/faker");

      // Function to generate a random address
      const generateAddress = (): Address => {
        return {
          address1: faker.location.streetAddress(),
          address2: "",
          city: faker.location.city(),
          zip: faker.location.zipCode(),
          state: faker.location.state(),
        };
      };

      // Function to generate a random phone number
      const generatePhone = (): Phone => {
        return {
          phoneNumber: faker.phone.number(),
          type: simpleFaker.helpers.arrayElement([
            "home",
            "mobile",
            "work",
            "other",
          ]),
        };
      };

      const firstName = faker.person.firstName();
      const lastName = faker.person.lastName();

      const fakeData: CreateTechnicianDto = {
        tenantId: tenantId,
        firstName: firstName,
        lastName: lastName,
        displayName: `${firstName} ${lastName}`,
        email: faker.internet.email({ firstName, lastName }),
        linkedUserId: faker.string.uuid(),
        homeAddress: generateAddress(),
        workAddress: generateAddress(),
        primaryPhone: generatePhone(),
        role: faker.helpers.arrayElement(["user", "admin"]),
      };

      return await technician.create(fakeData, tenantId);
    }
  },
};
